<template>
	<k-panel :class="[get_color]" :style="tile_style" :fill-height="fillHeight" :body-scrollable="false" class="elevation-0 ma-0 pa-0 ks-monitor-video">
		<template #title>
			<div style="display: grid; grid-template-columns: max-content minmax(0, 1fr); min-width: 0; color: var(--ks-font-color-primary);" class="ks-font-size-xs pl-1" @click.stop="emit_title_click">
				<div>
					<template v-if="iconColor">
						<k-icon :color="iconColor" small class="mr-2">circle</k-icon>
					</template>
				</div>
				<div ref="title_el" :key="title_tooltip" class="ks-monitor-video-title" v-tooltip.bottom="title_tooltip">
					<slot name="title"></slot>
				</div>
			</div>
		</template>
		<template #buttons>
			<span style="text-wrap: nowrap;">
				<!-- Video and audio tools share one menu so small tiles keep room for the title -->
				<k-menu v-if="has_tools_menu" right offset-y :close-on-content-click="false">
					<template #activator>
						<k-btn no-margin size="xs" icon icon-name="more_vert" :class="{'ks-monitor-video-active': diamond_on || frame_monitor_on || lufs_on || audio_meters_on}" v-tooltip.bottom="'Tools'"></k-btn>
					</template>
					<template #default>
						<div class="ks-monitor-video-tools">
							<k-btn v-if="diamondButton" list flat no-margin size="s" :class="{'ks-monitor-video-active': diamond_on}" @click="diamond_on = !diamond_on">
								<k-icon small inherit class="mr-2">diamond</k-icon>Diamond display
							</k-btn>
							<k-btn v-if="frameMonitorButton" list flat no-margin size="s" :class="{'ks-monitor-video-active': frame_monitor_on}" @click="frame_monitor_on = !frame_monitor_on">
								<k-icon small inherit class="mr-2">monitor_heart</k-icon>Freeze / black frames
							</k-btn>
							<k-btn v-if="frameMonitorButton" list flat no-margin size="s" @click="frame_history_open = true">
								<k-icon small inherit class="mr-2">history</k-icon>Frame history ({{frame_events.length}})
							</k-btn>
							<k-divider v-if="(diamondButton || frameMonitorButton) && lufsButton" class="mt-1 mb-1"></k-divider>
							<k-btn v-if="lufsButton" list flat no-margin size="s" :class="{'ks-monitor-video-active': audio_meters_on}" @click="audio_meters_on = !audio_meters_on">
								<k-icon small inherit class="mr-2">equalizer</k-icon>Audio meters
							</k-btn>
							<k-btn v-if="lufsButton" list flat no-margin size="s" :class="{'ks-monitor-video-active': lufs_on}" @click="lufs_on = !lufs_on">
								<k-icon small inherit class="mr-2">graphic_eq</k-icon>Loudness (LUFS)
							</k-btn>
							<k-btn v-if="lufsButton" list flat no-margin size="s" :class="{'ks-monitor-video-active': lufs_radar_on}" @click="toggle_lufs_radar">
								<k-icon small inherit class="mr-2">radar</k-icon>Loudness radar
							</k-btn>
							<!-- Target loudness: 0 LU on the radar scale -->
							<label v-if="lufsButton" class="ks-monitor-video-tools-row">
								<k-icon small inherit class="mr-2">tune</k-icon>Target
								<input
									class="ks-monitor-video-target"
									type="number"
									min="-40"
									max="0"
									step="1"
									:value="lufs_target"
									@change="set_lufs_target($event.target.value)"
								>
								LUFS
							</label>
						</div>
					</template>
				</k-menu>
				<k-label-color-picker v-if="colorKey" right :value="tile_color" tooltip="Tile color" @input="select_tile_color"></k-label-color-picker>
				<k-btn no-margin size="xs" no-margin icon :href="get_url" target="_blank" :disabled="!load_player" v-if="showUrl" icon-name="play_circle_outline"></k-btn>
				<k-btn no-margin size="xs" no-margin icon @click.stop="emit_edit" v-if="edit" icon-name="edit"></k-btn>
 				<k-btn no-margin size="xs" no-margin icon @click.stop="emit_close" v-if="closable" icon-name="close"></k-btn>
	    </span>
		</template>

		<template #default>
			<!-- Other content (e.g. instance stats) in a video sized, scrollable area instead of the player -->
			<div v-if="$scopedSlots.content" class="ks-monitor-video-content" :class="{'ks-monitor-video-content-fill': fillHeight}">
				<!-- 16:9 until the tile is given a height, then it fills the rest of the tile -->
				<div v-if="fillHeight" class="ks-monitor-video-content-inner">
					<slot name="content"></slot>
				</div>
				<k-responsive v-else :aspect-ratio="16/9">
					<div class="ks-monitor-video-content-inner">
						<slot name="content"></slot>
					</div>
				</k-responsive>
			</div>
    	<k-video-player v-else ref="player" class="elevation-0 pa-0" v-model="player_info" :url="get_url" :load-player="load_player" :auto-start="autoStart" :lufs="lufsButton && lufs_on" :audio-meters="lufsButton && audio_meters_on" :lufs-target="lufs_target" :lufs-radar="lufs_radar_on" @lufs-radar-change="lufs_radar_on = $event" :diamond="diamondButton && diamond_on" :frame-monitor="frameMonitorButton && frame_monitor_on" @frame-event="on_frame_event" @frame-monitor-click="frame_history_open = true" @frame-monitor-close="frame_monitor_on = false" @lufs-close="lufs_on = false" style=" margin-top: -8px; position: relative; z-index: 1; background-color: black;">
        <span style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 100%; text-align: center;" v-if="!load_player">{{playerText}}</span>
      </k-video-player>
			<!-- Freeze / black frame history; kept while the tile exists, also when the detector is off -->
			<k-dialog v-if="frameMonitorButton" v-model="frame_history_open" width="440" max-width="95vw">
				<k-panel class="ks-floating-menu">
					<template #title>
						Freeze / black frames
					</template>
					<template #buttons>
						<k-btn size="m" icon icon-name="close" @click="frame_history_open = false"></k-btn>
					</template>
					<template #default>
						<!-- The tile name may be long, so it gets its own line that can wrap -->
						<div class="ks-monitor-video-history-name"><slot name="title"></slot></div>
						<div v-if="!frame_events.length" class="ks-monitor-video-history-empty">No freeze or black frames detected</div>
						<div v-else class="ks-monitor-video-history-list">
							<table class="ks-monitor-video-history">
								<thead>
									<tr><th>Time</th><th>Type</th><th>Duration</th></tr>
								</thead>
								<tbody>
									<tr v-for="e in frame_history" :key="e.id">
										<td>{{format_event_time(e)}}</td>
										<td>{{e.type == 'freeze' ? 'Freeze' : 'Black'}}</td>
										<td :class="{'ks-monitor-video-history-ongoing': e.end === null}">{{e.end === null ? 'Ongoing' : format_event_duration(e)}}</td>
									</tr>
								</tbody>
							</table>
						</div>
						<div class="mt-2">
							<k-btn flat size="s" no-margin :disabled="!frame_events.length" v-tooltip.top="'Clear the history'" @click="frame_events = []">Clear</k-btn>
							<k-btn flat size="s" no-margin :disabled="!frame_monitor_on" v-tooltip.top="'Reset the counts shown over the video'" @click="reset_frame_counts">Reset counts</k-btn>
						</div>
					</template>
				</k-panel>
			</k-dialog>
		</template>

		<template #footer>
			<div class="mb-1" style="position: relative; margin-top: -8px; padding-top: 5px; margin-left: -8px; margin-right: -8px; padding-left: 8px; padding-right: 8px; height: 20px;">

				<div v-if="progress" :class="get_progress_color" :style="{'width': progressPercent + '%'}" style="border-radius: 6px; position: absolute; top: 10px; height: 12px; left: 0; bottom: 0; z-index: 1;"></div>

				<div class="ks-font-size-s" style="display: grid; grid-template-columns: 1fr 1fr; width: 100%; height: 20px; line-height: 15px; color: var(--ks-font-color-secondary);">
				<div>
   				<k-menu style="z-index: 2; min-width: 60px; line-height: 22px;" v-if="urls.length > 1">
   					<template #activator>

   						<div style="display:grid; grid-template-columns: 1fr 30px;">
								<div>{{ current_url.title }}</div>
								<div><k-icon small>arrow_drop_down</k-icon></div>
							</div>
   					</template>
   					<template #default>
   						<k-list :items="urls" @click="select_url">
   							<template #default="{ item }">
   								{{item.title}}
   							</template>
        			</k-list>
        		</template>
      		</k-menu>
      		<span v-if="showPlayerInfo && player_info.width && player_info.height" style="z-index: 2; line-height: 22px;">
      			{{player_info.width}} x {{player_info.height}}
      		</span>
      	</div>
	      <div :style="{'grid-template-columns': stoppable ? '1fr 16px' : '1fr', 'padding-right': stoppable ? '18px' : '0'}" style="display: grid; height: 24px; line-height: 22px; overflow: hidden;">
		      <span style="z-index: 2;">
  			    <slot name="subtitle"></slot>
	  		  </span>
      		<template v-if="stoppable">
      			<span style="z-index: 2;">
      				<slot name="stop"></slot>
      			</span>
      		</template>
      	</div>
    		</div>
    	</div>
		</template>
	</k-panel>
</template>

<script>

import { TILE_COLORS, tileColor } from '@/models/monitor.js';
import datelib from '@/lib/date.js';
import { format_duration } from '@/lib/frame_monitor.js';

// Max freeze / black frame events kept per tile
const MAX_FRAME_EVENTS = 500;

export default {
	props: {
		"urls": {
			type: Array,
			default: function() {return []}
		},
		"color": String,
		"icon-color": String,
		"stoppable": {
			type: Boolean,
			default: false
		},
		"closable": {
			type: Boolean,
			default: true
		},
		"stop-tooltip": String,
		"progress-percent": {
			type: Number,
			default: 0
		},
		"progress-warning": {
			type: Boolean,
			default: false
		},
		"progress": {
			type: Boolean,
			default: true
		},
		"show-player": {
			type: Boolean,
			default: true
		},
		"show-player-info": {
			type: Boolean,
			default: true
		},
		"player-text": {
			type: String,
			default: "No preview"
		},
		"edit": {
			type: Boolean,
			default: false
		},
		"edit-text": {
			type: String,
			default: "Edit"
		},
		"hide-footer": {
			type: Boolean,
			default: false
		},
		"show-url": {
			type: Boolean,
			default: true
		},
		// Shows a menu item that turns the freeze / black frame counter of the player on and off
		"frame-monitor-button": {
			type: Boolean,
			default: false
		},
		// Shows a button that turns the diamond display (RGB gamut) of the player on and off
		"diamond-button": {
			type: Boolean,
			default: false
		},
		// Shows buttons for the audio tools of the player: short-term loudness (LUFS), loudness radar and audio meters
		"lufs-button": {
			type: Boolean,
			default: false
		},
		// The panel fills the height of its parent and the content slot stretches to it
		// (for tiles with a free size; only used with the content slot)
		"fill-height": {
			type: Boolean,
			default: false
		},
		// Starts the video by itself when it is available
		"auto-start": {
			type: Boolean,
			default: false
		},
		// Enables the tile color picker; the selection is stored in account.meta.tile_colors under this key
		"color-key": {
			type: String,
			default: ""
		}
	},
	components: {
	},
	mounted() {
		this.update_title_tooltip();
	},
	updated() {
		this.update_title_tooltip();
	},
	data() {
		return {
			player_info: {width: 0, height: 0},
			lufs_on: false,
			audio_meters_on: false,
			lufs_radar_on: false,
			// EBU R128
			lufs_target: -23,
			diamond_on: false,
			frame_monitor_on: false,
			// [{id, type, start, end}] in epoch ms, end is null while the event goes on
			frame_events: [],
			frame_history_open: false,
			selected_title: null,
			title_tooltip: ""
		}
	},
	computed: {
		// Newest first
		frame_history() {
			return this.frame_events.slice().reverse();
		},
		has_tools_menu() {
			return this.diamondButton || this.frameMonitorButton || this.lufsButton;
		},
		tile_color() {
			return tileColor(this.$store.state.account, this.colorKey);
		},
		tile_style() {
			var c = TILE_COLORS.find(c => c.value && c.value === this.tile_color);
			return c ? { "--ks-panel-background-color": c.color } : {};
		},
		current_url() {
			var selected = this.urls.find(u => u.title === this.selected_title);
			return selected || this.urls[0] || {};
		},
		get_progress_color() {
			if (this.progressWarning == true) return "progress-warning";
			return "ks-monitor-video-color-success-progress";
		},
		get_color() {
			return "ks-monitor-video-color-" + this.color;
		},
		load_player() {
			return this.showPlayer && this.get_url != "";
		},
		get_url () {
			return this.current_url.url || "";
		}
	},
	watch: {
		// The radar needs the LUFS meter
		lufs_on (value) {
			if (!value) this.lufs_radar_on = false;
		}
	},
	methods: {
		// Resets the counts shown over the video; the history is kept
		reset_frame_counts () {
			if (this.$refs.player) this.$refs.player.reset_frame_monitor();
		},
		on_frame_event (e) {
			var i = this.frame_events.findIndex(f => f.id === e.id);
			if (i !== -1) {
				this.$set(this.frame_events, i, e);
				return;
			}
			this.frame_events.push(e);
			if (this.frame_events.length > MAX_FRAME_EVENTS) this.frame_events.splice(0, this.frame_events.length - MAX_FRAME_EVENTS);
		},
		// Date only when not today
		format_event_time (e) {
			var ts = e.start / 1000;
			var time = datelib.format_time_seconds(ts);
			return datelib.format_date(ts) === datelib.format_date() ? time : datelib.format_date(ts) + " " + time;
		},
		format_event_duration (e) {
			return format_duration(e.end - e.start);
		},
		set_lufs_target (value) {
			var v = Math.round(Number(value));
			this.lufs_target = isFinite(v) && value !== "" ? Math.min(0, Math.max(-40, v)) : -23;
			// Shows the cleaned value also when it didn't change (e.g. out of range input)
			this.$forceUpdate();
		},
		toggle_lufs_radar () {
			this.lufs_radar_on = !this.lufs_radar_on;
			if (this.lufs_radar_on) this.lufs_on = true;
		},
		update_title_tooltip () {
			var el = this.$refs.title_el;
			if (!el) return;
			var text = (el.textContent || "").replace(/\s+/g, " ").trim();
			// tooltip renders its value as HTML
			text = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
			if (text !== this.title_tooltip) this.title_tooltip = text;
		},
		select_tile_color (value) {
			if (!this.$store.state.account || value === this.tile_color) return;
			var acc = _.cloneDeep(this.$store.state.account);
			var colors = Object.assign({}, acc.meta.tile_colors);
			if (value) {
				colors[this.colorKey] = value;
			} else {
				delete colors[this.colorKey];
			}
			acc.meta.tile_colors = colors;
			this.$store.dispatch("update_profile", acc);
		},
		select_url (url) {
			this.selected_title = url.title;
		},
		emit_close () {
			this.$emit("close");
		},
		emit_title_click () {
			this.$emit("titleclick");
		},
		emit_edit () {
			this.$emit("edit");
		}
	}
}
</script>

<style>
	.ks-panel.ks-monitor-video > .ks-panel-heading {
		grid-template-columns: minmax(0, 1fr) max-content max-content;
	}
	.ks-panel.ks-monitor-video > .ks-panel-heading > .ks-panel-title {
		justify-self: stretch;
		min-width: 0;
		overflow: hidden;
	}
	.ks-monitor-video-title {
		min-width: 0;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}
</style>

<style scoped>
	/* .elevation-0 sets the border with !important */
	.ks-monitor-video {
		isolation: isolate;
		border-color: var(--ks-monitor-video-border-color, var(--ks-col-secondary)) !important;
	}

.ks-monitor-video-tools {
		display: flex;
		flex-direction: column;
		padding: 4px 0;
	}
	.ks-monitor-video-history-name {
		margin-bottom: 8px;
		color: var(--ks-font-color-secondary);
		overflow-wrap: anywhere;
	}
	.ks-monitor-video-history-empty {
		padding: 8px 0;
		color: var(--ks-font-color-secondary);
	}
	.ks-monitor-video-history-list {
		max-height: 60vh;
		overflow-y: auto;
	}
	.ks-monitor-video-history {
		width: 100%;
		border-collapse: collapse;
		font-size: var(--ks-font-size-s);
		font-variant-numeric: tabular-nums;
	}
	.ks-monitor-video-history th {
		position: sticky;
		top: 0;
		text-align: left;
		font-weight: 500;
		color: var(--ks-font-color-secondary);
		background: var(--ks-panel-background-color);
	}
	.ks-monitor-video-history th,
	.ks-monitor-video-history td {
		padding: 4px 8px 4px 0;
		border-bottom: 1px solid var(--ks-col-secondary);
	}
	.ks-monitor-video-history-ongoing {
		color: var(--ks-col-danger);
	}
	.ks-monitor-video-content {
		position: relative;
		z-index: 1;
		margin-top: -8px;
		background: var(--ks-background-color-dark);
	}
	.ks-monitor-video-content-inner {
		box-sizing: border-box;
		height: 100%;
		padding: 4px 8px;
		overflow: auto;
	}
	/* 16:9 with an automatic tile height; grows and shrinks with a fixed tile height */
	.ks-monitor-video-content-fill {
		flex: 1 1 auto;
		min-height: 0;
		aspect-ratio: 16 / 9;
		display: flex;
		flex-direction: column;
	}
	.ks-monitor-video-content-fill > .ks-monitor-video-content-inner {
		flex: 1 1 auto;
		min-height: 0;
		height: auto;
	}
	.ks-monitor-video-tools-row {
		display: flex;
		align-items: center;
		padding: 2px 8px;
		font-size: var(--ks-font-size-s);
		color: var(--ks-font-color-primary);
	}
	.ks-monitor-video-target {
		width: 4.5em;
		margin: 0 6px 0 auto;
		box-sizing: border-box;
		padding: 2px 4px;
		border: 1px solid var(--ks-col-secondary);
		border-radius: 3px;
		background: var(--ks-background-color-dark);
		color: inherit;
		font: inherit;
		text-align: right;
		outline: none;
	}
	.ks-monitor-video-target:focus {
		border-color: var(--ks-col-accent);
	}
	.ks-monitor-video-tools .ks-btn {
		font-size: var(--ks-font-size-s);
	}
	/* The theme has no accent color for icon buttons; the icon inherits the button color */
	.ks-monitor-video-active {
		color: var(--ks-col-accent);
	}

	.ks-monitor-video-color-success {
		--ks-monitor-video-border-color: var(--ks-col-success-dark);
	}

	.ks-monitor-video-color-danger {
		--ks-monitor-video-border-color: var(--ks-col-danger-dark);
	}
	.ks-monitor-video-color-warning {
		--ks-monitor-video-border-color: var(--ks-col-warning);
	}

	.ks-monitor-video-color-primary {
		--ks-monitor-video-border-color: var(--ks-col-secondary);
	}
	.ks-monitor-video-color-past {
		--ks-monitor-video-border-color: var(--ks-col-secondary);
		opacity: 0.3;
	}

	.ks-monitor-video-color-success-progress {
		background: var(--ks-col-secondary);
	}


	.progress-warning {
		animation: rotate-bg-color-dark 0.7s infinite;
	}
	@keyframes rotate-bg-color-dark {
    	0% {
      		background-color: var(--ks-background-color);
    	}
    	25% {
      		background-color: var(--ks-col-danger-dark);
    	}
    	50% {
     		background-color: var(--ks-col-danger);
    	}
    	75% {
      		background-color: var(--ks-col-danger-dark);
    	}
    	100% {
      		background-color: var(--ks-background-color);
    	}
	}
</style>
